import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, createContext, useEffect, useMemo, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { generateUniqueId } from '../../utils/generateUniqueId';
import { useIsMounted } from '../../utils/useIsMounted';
import { useStateInRef } from '../../utils/useStateInRef';
import { useBiContextGetter } from '../BiTracking/BiContext';
import { BottomSheet } from '../BottomSheet';
import { ConfirmationModal } from '../ConfirmationModal';
import { useDevice } from '../Device';
import { useTheme } from '../Theme';
import { Toast } from '../Toast';
import { GlobalLoader } from './GlobalLoader';
import { ModalContainer } from './ModalContainer';
import styles from './OverlayContext.module.scss';
const noop = ()=>{};
export const OverlayLoaderTID = 'async-backdrop';
const OverlayContext = /*#__PURE__*/ createContext({
    isTestMode: false,
    hasOpenModals: false,
    getOpenedModals: ()=>[],
    showModal: async ()=>undefined,
    showConfirmAction: async ()=>Promise.resolve(),
    hideModal: noop,
    hideAllModals: noop,
    startLoader: noop,
    stopLoader: noop,
    showErrorToast: noop,
    showSuccessToast: noop,
    showInfoToast: noop,
    showBottomSheet: noop,
    pushBottomSheetPage: noop,
    popBottomSheetPage: noop,
    hideBottomSheet: noop
});
export function OverlayProvider(props) {
    const { children, ModalWrapper, isTestMode = false, disableEnforceFocus } = props;
    const isMounted = useIsMounted();
    const [modals, setModals] = useState([]);
    const openModals = modals.filter((m)=>m.isOpen);
    const getOpenModals = useStateInRef(openModals);
    const [loaders, setLoaders] = useState({});
    const [toasts, setToasts] = useState([]);
    const [bottomSheetPages, setBottomSheetPages] = useState([]);
    const [bottomSheetIsClosing, setBottomSheetIsClosing] = useState(false);
    const currentBottomSheetPage = bottomSheetPages[bottomSheetPages.length - 1];
    const somethingIsLoading = Object.values(loaders).some((count)=>count > 0);
    const shouldBlurBackground = modals.some((modal)=>modal.shouldBlurBackground);
    const hasOpenModals = openModals.length > 0;
    useEffect(()=>{
        return ()=>{
            setToasts((prevToasts)=>{
                prevToasts.forEach((toast)=>clearTimeout(toast.timer));
                return prevToasts;
            });
        };
    }, []);
    const staticMethods = useMemo(()=>{
        function popBottomSheetPage() {
            setBottomSheetPages((prev)=>prev.length > 1 ? prev.slice(0, -1) : []);
        }
        function hideBottomSheet() {
            setBottomSheetIsClosing(true);
        }
        function pushBottomSheetPage(config) {
            setBottomSheetPages((prev)=>[
                    ...prev,
                    config
                ]);
        }
        function showBottomSheet(config) {
            setBottomSheetIsClosing(false);
            setBottomSheetPages([
                config
            ]);
        }
        function showToast(content) {
            const defaultDuration = 5;
            const id = "toast".concat(new Date().getTime());
            const transitionDuration = "".concat(defaultDuration * 0.95, "s");
            const timer = setTimeout(()=>{
                isMounted() && setToasts((prevToasts)=>prevToasts.filter((toast)=>toast.id !== id));
            }, defaultDuration * 1000);
            setToasts((prevToasts)=>prevToasts.concat({
                    content,
                    id,
                    transitionDuration,
                    timer
                }));
        }
        const showModal = (params)=>{
            return new Promise((resolve)=>{
                const { openBottomSheetOnMobile, device, hideBottomSheetOnDismiss } = params;
                if (openBottomSheetOnMobile && (device === null || device === void 0 ? void 0 : device.isMobileDevice)) {
                    pushBottomSheetPage({
                        content: typeof params.content === 'function' ? params.content({
                            dismissModal: ()=>{
                                if (hideBottomSheetOnDismiss) {
                                    hideBottomSheet();
                                } else {
                                    popBottomSheetPage();
                                }
                                resolve(undefined);
                            },
                            registerCanDismiss: ()=>{}
                        }) : undefined
                    });
                } else {
                    setModals((prevModals)=>{
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const newModal = _object_spread_props(_object_spread({}, params), {
                            id: generateUniqueId('modal'),
                            isOpen: true,
                            resolve,
                            dismissModal: ()=>{}
                        });
                        return prevModals.concat(newModal);
                    });
                }
            });
        };
        const showConfirmAction = async (param)=>{
            let { testId, onConfirm, title, content, primaryButtonLabel, secondaryButtonLabel, textAlignCenter = false, width, skin } = param;
            await showModal({
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(ConfirmationModal, {
                        testId: testId,
                        skin: skin,
                        title: title,
                        content: content,
                        textAlignCenter: textAlignCenter,
                        width: width,
                        primaryButtonLabel: primaryButtonLabel,
                        secondaryButtonLabel: secondaryButtonLabel,
                        onPrimaryButtonClick: async ()=>{
                            await (onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm());
                            dismissModal(true);
                        },
                        onSecondaryButtonClick: ()=>dismissModal(false)
                    });
                }
            });
        };
        return {
            getOpenedModals () {
                return getOpenModals();
            },
            showModal,
            showConfirmAction,
            hideModal (value) {
                const openModals = getOpenModals();
                if (!openModals.length) return;
                openModals[openModals.length - 1].dismissModal(value);
            },
            hideAllModals (value) {
                for (const modal of getOpenModals()){
                    modal.dismissModal(value);
                }
            },
            startLoader: (name)=>{
                const actualName = name || 'undefined';
                setLoaders((prevLoaders)=>_object_spread_props(_object_spread({}, prevLoaders), {
                        [actualName]: (prevLoaders[actualName] || 0) + 1
                    }));
            },
            stopLoader: (name)=>{
                const actualName = name || 'undefined';
                setLoaders((prevLoaders)=>_object_spread_props(_object_spread({}, prevLoaders), {
                        [actualName]: Math.max(0, (prevLoaders[actualName] || 0) - 1)
                    }));
            },
            showErrorToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "error",
                    text: text,
                    testId: testId
                })),
            showSuccessToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "success",
                    text: text,
                    testId: testId
                })),
            showInfoToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "info",
                    text: text,
                    testId: testId
                })),
            showBottomSheet,
            pushBottomSheetPage,
            popBottomSheetPage,
            hideBottomSheet
        };
    }, []);
    const contextValue = useMemo(()=>_object_spread_props(_object_spread({}, staticMethods), {
            isTestMode,
            hasOpenModals
        }), [
        isTestMode,
        hasOpenModals,
        staticMethods
    ]);
    function onModalExited(id) {
        setModals((prevModals)=>prevModals.filter((m)=>m.id !== id));
    }
    function triggerClose(id) {
        setModals((prevModals)=>prevModals.map((m)=>m.id === id ? _object_spread_props(_object_spread({}, m), {
                    isOpen: false
                }) : m));
    }
    return /*#__PURE__*/ _jsxs(OverlayContext.Provider, {
        value: contextValue,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: cn(styles.overlayContext, {
                    [styles.blur]: shouldBlurBackground
                }),
                children: children
            }),
            modals.map((modal)=>/*#__PURE__*/ _jsx(ModalContainer, {
                    modal: modal,
                    ModalWrapper: ModalWrapper,
                    isTestMode: isTestMode,
                    disableEnforceFocus: disableEnforceFocus,
                    triggerClose: triggerClose,
                    onModalExited: onModalExited
                }, modal.id)),
            /*#__PURE__*/ _jsx("div", {
                className: styles.toastsContainer,
                children: toasts.map((param)=>{
                    let { id, content, transitionDuration } = param;
                    return /*#__PURE__*/ _jsx("div", {
                        className: styles.toast,
                        style: {
                            '--toastDuration': transitionDuration
                        },
                        children: content
                    }, id);
                })
            }),
            /*#__PURE__*/ _jsx(BottomSheet, {
                open: bottomSheetPages.length > 0 && !bottomSheetIsClosing,
                showBackButton: bottomSheetPages.length > 1,
                paddingBottom: currentBottomSheetPage === null || currentBottomSheetPage === void 0 ? void 0 : currentBottomSheetPage.paddingBottom,
                onClose: ()=>{
                    staticMethods.hideBottomSheet();
                },
                onBack: staticMethods.popBottomSheetPage,
                onExited: ()=>{
                    var _currentBottomSheetPage_onClose;
                    setBottomSheetPages([]);
                    setBottomSheetIsClosing(false);
                    currentBottomSheetPage === null || currentBottomSheetPage === void 0 ? void 0 : (_currentBottomSheetPage_onClose = currentBottomSheetPage.onClose) === null || _currentBottomSheetPage_onClose === void 0 ? void 0 : _currentBottomSheetPage_onClose.call(currentBottomSheetPage);
                },
                children: bottomSheetPages.map((page, idx)=>/*#__PURE__*/ _jsx("div", {
                        style: {
                            display: idx === bottomSheetPages.length - 1 ? 'contents' : 'none'
                        },
                        children: page.content
                    }, idx))
            }),
            somethingIsLoading && /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ _jsx(GlobalLoader, {}), document.body)
        ]
    });
}
export function useOverlayContext() {
    const theme = useTheme();
    const getDevice = useStateInRef(useDevice());
    const _useContext = useContext(OverlayContext), { showModal } = _useContext, rest = _object_without_properties(_useContext, [
        "showModal"
    ]);
    const getBiContext = useBiContextGetter();
    // useCallback ensures showModalWithBi is stable (same reference across renders)
    // getBiContext is already stable, so this callback is also stable
    const showModalWithBi = useCallback((config)=>{
        var _config_biCallerContext;
        return showModal(_object_spread_props(_object_spread({}, config), {
            theme,
            device: getDevice(),
            biCallerContext: (_config_biCallerContext = config.biCallerContext) !== null && _config_biCallerContext !== void 0 ? _config_biCallerContext : getBiContext
        }));
    }, [
        showModal,
        theme,
        getBiContext
    ]);
    return _object_spread_props(_object_spread({}, rest), {
        showModal: showModalWithBi
    });
}
